import { createApiUrl } from "@/lib/api/create-api-url";
import { useAuthStore } from "@/lib/auth/auth-store";
import {
  handleSessionExpired,
  isReauthRequiredError,
} from "@/lib/auth/session-expiry";

export class ApiError extends Error {
  status: number;
  response?: Response;
  body?: unknown;

  constructor(
    status: number,
    message: string,
    response?: Response,
    body?: unknown,
  ) {
    super(message);
    this.status = status;
    this.response = response;
    this.body = body;
  }
}

export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, "Your session has expired. Please sign in again.");
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ErrorType<E = unknown> = ApiError;

export const authenticatedClient = async <T>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  if (typeof window === "undefined") {
    throw new ApiError(
      0,
      "Backend API requests must run in the browser client.",
    );
  }

  const fullUrl = url.startsWith("http") ? url : createApiUrl(url);

  const headers = new Headers(options.headers);
  headers.set("x-service-name", "clarityos-fe");

  if (!headers.has("Authorization")) {
    const { getAccessTokenSilently } = useAuthStore.getState();
    if (getAccessTokenSilently) {
      try {
        const token = await getAccessTokenSilently();
        headers.set("Authorization", `Bearer ${token}`);
      } catch (error) {
        if (isReauthRequiredError(error)) {
          handleSessionExpired();
          throw new SessionExpiredError();
        }
        throw error;
      }
    }
  }

  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const { message, body } = await parseErrorResponse(response);
    throw new ApiError(response.status, message, response, body);
  }

  const data = await getResponseBody(response);

  return { data, status: response.status, headers: response.headers } as T;
};

const parseErrorResponse = async (
  response: Response,
): Promise<{ message: string; body?: unknown }> => {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorBody = await response.json();

      if (
        errorBody &&
        typeof errorBody === "object" &&
        "message" in errorBody &&
        typeof errorBody.message === "string"
      ) {
        return { message: errorBody.message, body: errorBody };
      }

      if (
        errorBody &&
        typeof errorBody === "object" &&
        "error" in errorBody &&
        typeof errorBody.error === "string"
      ) {
        return { message: errorBody.error, body: errorBody };
      }

      return { message: response.statusText, body: errorBody };
    }
  } catch {
    // Ignore parsing errors and fall back to status text below.
  }

  return { message: response.statusText };
};

const getResponseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }

  const isBlobType =
    contentType &&
    (contentType.includes("application/pdf") ||
      contentType.includes("application/octet-stream") ||
      contentType.startsWith("image/") ||
      contentType.includes("text/csv") ||
      contentType.includes("application/vnd."));

  if (isBlobType) {
    return response.blob();
  }

  const text = await response.text();
  return text || undefined;
};
